import React, { forwardRef, MouseEvent, ReactElement } from 'react';

import PropTypes from 'prop-types';

import { LinkButton } from '@adsk/alloy-react-button';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { useTranslation } from '../../i18n';

type Value = string;
type CreateNewMenuItemProps = StylableComponent<HTMLLIElement> & {
  value: Value;
  isHighlighted?: boolean;
  onCreate?: (value: Value, e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optional custom label for the create new item.
   * Automatically will replace {{label}} symbol with the value.
   */
  label?: string;
};

const CreateNewMenuItem = forwardRef<HTMLLIElement, CreateNewMenuItemProps>(
  (
    { className, style, value, isHighlighted, onCreate, label, ...props },
    ref,
  ): ReactElement => {
    const { t } = useTranslation();
    const { tokens } = useTheme();
    const createNewTokens = tokens?.component.dropdown?.menu?.createNew;

    const _label = React.useMemo(
      () =>
        label
          ? label.replace('{{label}}', value)
          : t('CREATE_OPTION', {
              label: value,
              interpolation: { escapeValue: false },
            }),
      [label, value, t],
    );

    return (
      <li
        ref={ref}
        className={className}
        css={[
          createNewTokens?._,
          isHighlighted && createNewTokens?.highlighted?.true,
          { '&:active': createNewTokens?.pressed },
          style,
        ]}
        {...props}
      >
        <LinkButton
          onClick={(e) => {
            onCreate?.(value, e);
          }}
        >
          <span dir="auto" css={createNewTokens?.label}>
            {_label}
          </span>
        </LinkButton>
      </li>
    );
  },
);

CreateNewMenuItem.displayName = 'CreateNewMenuItem';

CreateNewMenuItem.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
  onCreate: PropTypes.func,
};

export default CreateNewMenuItem;
